import {useCallback, useEffect, useState} from "react";
import {damsFetch} from "../app/damsFetch";
import {kitFetch} from "@ekultur/fetch";
import {clientLog} from "../clientLog";
import {
    SET_DAMS_ID,
    SET_MUSEUM_COLLECTIONS,
    SET_MUSEUM_FEATURES,
    SET_USER_DATA,
    useAuthsDispatch,
    useAuthsState
} from "./authsContext";
import {getI18n} from "react-i18next";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import {useAppTranslation} from "../app/AppContext";
import {Alert, Dialog, DialogContent} from "@mui/material";
import {createIndexedDb} from "../indexeddb/createIndexedDb";

/**
 * @description
 * Higher order component that fetches and sets user data and other related data.
 * If the user is not authenticated, it will not render the children.
 *
 * The component will fetch and set the following data:
 * - User data
 * - Museum collections
 * - Dams ID
 * - Museum features (if the user has access to the archeology module)
 *
 * @param {ReactNode} children - The children to render.
 *
 * @returns {React.JSX.Element} - The children if the user is authenticated, otherwise an empty fragment.
 */
export const WithUserData = ({children}) => {

    const t = useAppTranslation();
    const {userData, userIsAuthenticated} = useAuthsState();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const authsDispatch = useAuthsDispatch();

    const handleError = error => {
        clientLog('error', error.message || error, 'withuserdata');
        setError(true);
    };

    const authsDispatchCallback = useCallback(
        (action) => authsDispatch(action),
        [authsDispatch]
    );

    const getMuseumCollections = useCallback(async () => {
        try {
            return await damsFetch('/collections/my-museum-collections/');
        } catch (error) {
            handleError(error);
        }
    }, []);

    const setUserDamsId = useCallback(async () => {
        try {
            const data = await damsFetch('/current-user/my-dams-id/');
            authsDispatchCallback({
                type: SET_DAMS_ID,
                damsId: data['id']
            });
        } catch (error) {
            handleError(error);
        }
    }, [authsDispatchCallback]);

    const setMuseumsWithArcheologyModuleEnabled = useCallback(async (collectionData) => {
        try {
            const data = await kitFetch('/museum-api/feature-toggles/DAMSArkeologi/');
            const museumsWithArcheologyFeature = data.map(d => d.museumId);
            const museums = collectionData
                .map(c => ({id: c.museumId}))
                .map(m => ({
                    ...m,
                    features: {
                        archeology: museumsWithArcheologyFeature.includes(m.id)
                    }
                }));
            authsDispatch({type: SET_MUSEUM_FEATURES, museumFeatures: museums});
        } catch (error) {
            handleError(error);
        }
    }, [authsDispatch]);

    const authorizeUser = useCallback(async () => {
        try {
            const userData = await kitFetch('/authz/authorize/userData');

            authsDispatchCallback({
                type: SET_USER_DATA,
                userData: userData
            });

            const collectionData = await getMuseumCollections();

            if (!collectionData) {
                return;
            }

            authsDispatchCallback({
                type: SET_MUSEUM_COLLECTIONS,
                museumCollections: collectionData
            });

            await setMuseumsWithArcheologyModuleEnabled(collectionData);
            await setUserDamsId()

            const {name, locale = undefined} = userData;

            await updateCurrentUser(name);
            if (locale) {
                await getI18n().changeLanguage(locale);
            }

            setLoading(false);

        } catch (error) {
            handleError(error);
        }
    }, [authsDispatchCallback, setMuseumsWithArcheologyModuleEnabled, setUserDamsId, getMuseumCollections]);

    const updateCurrentUser = async (username) => {
        return await damsFetch('/current-user/', {
            method: 'POST',
            body: JSON.stringify({name: username})
        });
    };

    useEffect(() => {
        if (!userIsAuthenticated || Object.keys(userData).length > 0) {
            return;
        }
        (async () => await authorizeUser())();
    }, [
        userIsAuthenticated,
        userData,
        authorizeUser
    ]);

    /**
     * Hook used to create and populate IndexedDB object stores:
     */
    useEffect(() => {
        if (!userIsAuthenticated) {
            return;
        }
        (async () => {
            await createIndexedDb();
        })();
    }, [userIsAuthenticated]);


    if (error) {
        return <Dialog open={error}>
            <DialogContent>
                <Alert
                    severity="error" color="error"
                    sx={{
                        textWrap: 'pretty',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxWidth: '450px'
                    }}
                >
                    {t('errorUnableToInitializeApp',
                        'Det oppstod en feil under initialisering av applikasjonen. Vennligst prøv igjen senere.')}
                </Alert>
            </DialogContent>
        </Dialog>
    } else if (!loading && Object.keys(userData).length > 0) {
        return <>{children}</>;
    } else if (loading) {
        return <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100vw',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
            <LinearProgress variant="indeterminate" color="primary" sx={{width: '70%', marginBottom: '16px'}}/>
            <Typography>{t('initializingApp', 'Initialiserer applikasjonen...')}</Typography>
        </Box>;
    } else {
        return <></>;
    }
};